import { PublicClientApplication } from "@azure/msal-browser";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import * as z from 'zod';
import { CookiesKeys, LoginPlatforms, pathDelmiter, ssoProperties, ssoPropertiesCIAM } from '../Constants/Consts';
import { msalConfig } from "../config/MsalConfig";
import { ChecklistTaskApiResponse, DDCheckList, DrilldownMappingAPIResponse, TaskNode, TasksRoot } from "../types/DataDD/DataDD";


export const splitOnCapitals = (name: string = "") => name.split(/(?=[A-Z])/g)?.join(" ")
export const commaSeparatedNumbers = (nums: String = "") => nums.split(",")?.map(n => Number(n))


// to intersept when needed
export const fetchApi = async (url: string, options?: RequestInit) => {
  const currentToken = Cookies.get(CookiesKeys.jwt)
  const decodedToken = jwtDecode(currentToken);
  const currentTime = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = decodedToken.exp - currentTime;
  if (timeUntilExpiry <= 300) { // 5 minutes threshold
    // Token is about to expire, renew it
    const msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize()
    const accounts = msalInstance.getAllAccounts()
    if (Cookies.get(CookiesKeys.loginPlatform) === LoginPlatforms.azure) {
      const accessTokenRequest = {
        scopes: ssoProperties.scopes,
        account: accounts[0],
      };
      const newTokenResponse = await msalInstance.acquireTokenSilent(accessTokenRequest);
      Cookies.set(CookiesKeys.jwt, newTokenResponse.accessToken);
    }
    else if (Cookies.get(CookiesKeys.loginPlatform) === LoginPlatforms.ciam) {
      const accessTokenRequest = {
        scopes: ssoPropertiesCIAM.scopes,
        account: accounts[0],
      };
      const newTokenResponse = await msalInstance.acquireTokenSilent(accessTokenRequest);
      Cookies.set(CookiesKeys.jwt, newTokenResponse.idToken);
    }
    else {
      //TODO redirect to landing page
      alert("you are not logged on")
    }
  }
  return fetch(url, {
    ...options,
    headers: {
      ...options?.headers,
      'Authorization': 'Bearer ' + Cookies.get(CookiesKeys.jwt),
    }
  })
};

export const zodKeys = <T extends z.ZodTypeAny>(schema: T): string[] => {
  if (schema === null || schema === undefined) return [];
  if (schema instanceof z.ZodNullable || schema instanceof z.ZodOptional) return zodKeys(schema.unwrap());
  if (schema instanceof z.ZodArray) return zodKeys(schema.element);
  if (schema instanceof z.ZodObject) {
    const entries = Object.entries(schema.shape);
    return entries.flatMap(([key, value]) => {
      const nested = value instanceof z.ZodType ? zodKeys(value).map(subKey => `${key}.${subKey}`) : [];
      return nested.length ? nested : key;
    });
  }
  return [];
};

const uuid = (a?): string => a ? (a ^ Math.random() * 16 >> a / 4).toString(16) : ([1e7, -1e3, -4e3, -8e3, -1e11].join('')).replace(/[018]/g, uuid);

export const getRandomUuid = () => crypto?.randomUUID?.() ?? uuid()

export const blobToBase64 = (blob): Promise<string> => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return new Promise(resolve => {
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
  });
};


export function deleteAllCookies() {
  document.cookie.split(';').forEach(cookie => {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
  });
}


export function getByPath(path: string = "", obj: any) {
  const pathArray = path.split(pathDelmiter).filter(Boolean);
  let current = obj;
  for (const key of pathArray) {
    if (current[key] === undefined) {
      return current;
    }
    current = current[key];
  }
  return current;
}

export function getCompletePath(ChecklistItem: DDCheckList | ChecklistTaskApiResponse) {
  switch (ChecklistItem.drilldownTypeId) {
    case 1:
      return `${ChecklistItem.dd01}.${ChecklistItem.dd02}.${ChecklistItem.ctUuid}`
    case 2:
    case 4:
      return `${ChecklistItem.dd01}.${ChecklistItem.dd02}.${ChecklistItem.dd03}.${ChecklistItem.ctUuid}`
    default:
      return `${ChecklistItem.dd01}.${ChecklistItem.dd02}.${ChecklistItem.ctUuid}`
  }
}
export function getCompletePath2(ChecklistItem: DDCheckList | ChecklistTaskApiResponse) {
  const steps = getDrilldownStepsNumber(ChecklistItem)
  let path = "childs"
  for (let i = 1; i <= steps; i++) {
    const atLastStep = i === steps
    const step = i < 10 ? `0${i}` : `${i}`
    const currentDD = ChecklistItem[`dd${step}`] // actual naming like CC20 -> CZA_540 -> ___
    path += pathDelmiter + currentDD
    if (atLastStep) {
      path += pathDelmiter + "taskChilds"
    } else path += pathDelmiter + "childs"
    if (atLastStep) path += pathDelmiter + ChecklistItem.ctUuid
  }
  return path
}

export function getDrilldownStepsNumber(ddo: DrilldownMappingAPIResponse | ChecklistTaskApiResponse) {
  let num = 0
  const ddoArray = Object.keys(ddo ?? {})
  for (const key of ddoArray) {
    if (key.includes("dd") && !!ddo[key]) {
      num++
    }
  }
  return num
}

export const buildChunks = (arr: string[], chunkSize: number) => Array.from(
  new Array(Math.ceil(arr.length / chunkSize)),
  (_, i) => arr.slice(i * chunkSize, i * chunkSize + chunkSize)
);

export function getTaskNodeAtSpecificLevelFromElement(element: ChecklistTaskApiResponse, rootObject: TasksRoot, level: number) {
  let currentObject: Record<string, ChecklistTaskApiResponse | TaskNode> = rootObject.childs;
  for (let i = 0; i < level; i++) {
    const dropDownKeyOfElement = element[`dd0${i + 1}`];
    if (!(currentObject[dropDownKeyOfElement] as TaskNode)?.childs) {
      throw new Error(`Invalid path at dropDownKeyOfElement: ${dropDownKeyOfElement}`);
    }
    currentObject = (currentObject[dropDownKeyOfElement] as TaskNode).childs;
  }
  return currentObject
}
export function getTaskNodeAtSpecificLevelFromElement2(element: ChecklistTaskApiResponse, rootObject: TaskNode, level: number) {
  let currentObject: Record<string, ChecklistTaskApiResponse | TaskNode> = rootObject.childs;
  for (let i = 0; i < level; i++) {
    const dropDownKeyOfElement = element[`dd0${i + 1}`];
    if (!(currentObject[dropDownKeyOfElement] as TaskNode)?.childs) {
      throw new Error(`Invalid path at dropDownKeyOfElement: ${dropDownKeyOfElement}`);
    }
    currentObject = (currentObject[dropDownKeyOfElement] as TaskNode).childs;
  }
  return currentObject
}

export function replaceLastOccurrence(path, delimiter, oldValue, newValue) {
  let parts = path.split(delimiter);
  for (let i = parts.length - 1; i >= 0; i--) {
    if (parts[i] === oldValue) {
      parts[i] = newValue;
      break;
    }
  }
  return parts.join(delimiter);
}

export function getDateMMDDYYYYFormat(date: Date) {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  return new Intl.DateTimeFormat('en-US', options).format(date);
}

export function setLocalStorageItem(key: string, value: any) {
  if (!key) return;
  localStorage.setItem(key, JSON.stringify(value));
}

export function getLocalStorageItem<T>(key: string): T | null {
  if (!key) return null;
  const item = JSON.parse(localStorage.getItem(key))
  return item ?? null;
}

export function removeLocalStorageItem(key: string) {
  if (!key) return;
  localStorage.removeItem(key);
}